import { useEffect, useState } from "react";
import {
  ConfirmDialog,
  FormCard,
  FormDialog,
  FormField,
  FormGrid,
  FormSearchSelect,
  FormSection,
} from "@NP-Webber/crm-kit";
import "@NP-Webber/crm-kit/dist/formkit/FormKit.css";
import {
  meetingsApi,
  type AssignableUser,
  type DefaultParticipant,
} from "../../../api/meetingsApi";
import { toClientApiError } from "../../../api/httpClient";
import { MEETING_ROLE_LABELS, MEETING_ROLE_OPTIONS } from "../constants";

type Props = {
  open: boolean;
  onClose: () => void;
  users?: AssignableUser[];
  onSaved?: () => void;
};

export function DefaultParticipantsDialog({
  open,
  onClose,
  users = [],
  onSaved,
}: Props) {
  const [rows, setRows] = useState<DefaultParticipant[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [addUserId, setAddUserId] = useState("");
  const [addRole, setAddRole] = useState("member");
  const [deleteIdx, setDeleteIdx] = useState<number | null>(null);

  useEffect(() => {
    if (!open) return;
    setError("");
    setLoading(true);
    meetingsApi
      .getDefaultParticipants()
      .then((items) => setRows(items || []))
      .catch((err) => setError(toClientApiError(err).message))
      .finally(() => setLoading(false));
  }, [open]);

  const handleAdd = () => {
    if (!addUserId) return;
    if (rows.some((r) => String(r.userId) === String(addUserId))) return;
    const u = users.find((x) => String(x.id) === String(addUserId));
    setRows([
      ...rows,
      {
        userId: addUserId,
        userName: u?.name,
        meetingRole: addRole,
        participantKind: "regular",
      },
    ]);
    setAddUserId("");
    setAddRole("member");
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      await meetingsApi.updateDefaultParticipants(
        rows.map((r, i) => ({
          userId: r.userId,
          meetingRole: r.meetingRole,
          participantKind: r.participantKind || "regular",
          sortOrder: i + 1,
        })),
      );
      onSaved?.();
      onClose();
    } catch (err) {
      setError(toClientApiError(err).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <FormDialog open={open} onClose={onClose} size="md">
        <FormCard
          title="משתתפים קבועים"
          info="משתתפים אלה יועתקו אוטומטית לכל ישיבה חדשה"
          error={error}
          onSave={() => {
            void handleSave();
          }}
          onCancel={onClose}
          saving={saving}
          saveLabel="שמור תבנית"
          cancelLabel="ביטול"
        >
          {loading ? (
            <div className="tm-loading">טוען…</div>
          ) : (
            <>
              <FormSection title="הוספת משתתף">
                <FormGrid cols={2}>
                  <FormSearchSelect
                    label="משתמש"
                    options={users}
                    value={users.find((u) => String(u.id) === String(addUserId)) || null}
                    onChange={(v) =>
                      setAddUserId((v as AssignableUser | null)?.id || "")
                    }
                    getOptionLabel={(o) => (o as AssignableUser).name}
                    isOptionEqual={(a, b) =>
                      (a as AssignableUser | null)?.id ===
                      (b as AssignableUser | null)?.id
                    }
                    placeholder="בחר…"
                  />
                  <FormField
                    label="תפקיד"
                    value={addRole}
                    onChange={setAddRole}
                    options={MEETING_ROLE_OPTIONS}
                  />
                </FormGrid>
                <button
                  type="button"
                  className="tm-btn tm-btn--outline tm-btn--sm"
                  style={{ marginTop: 8 }}
                  onClick={handleAdd}
                >
                  הוסף לרשימה
                </button>
              </FormSection>
              <FormSection title={`רשימה (${rows.length})`}>
                {!rows.length ? (
                  <div className="tm-empty">אין משתתפים קבועים</div>
                ) : (
                  <div className="tm-agenda-list">
                    {rows.map((r, idx) => (
                      <div key={`${r.userId}-${idx}`} className="tm-agenda-item">
                        <div className="tm-agenda-item__body">
                          <div className="tm-agenda-item__title">
                            {r.userName || `#${r.userId}`}
                          </div>
                          <div className="tm-agenda-item__meta">
                            {MEETING_ROLE_LABELS[r.meetingRole] || r.meetingRole}
                          </div>
                        </div>
                        <button
                          type="button"
                          className="tm-icon-btn tm-icon-btn--danger"
                          onClick={() => setDeleteIdx(idx)}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </FormSection>
            </>
          )}
        </FormCard>
      </FormDialog>
      <ConfirmDialog
        open={deleteIdx !== null}
        onClose={() => setDeleteIdx(null)}
        onConfirm={() => {
          if (deleteIdx === null) return;
          setRows(rows.filter((_, i) => i !== deleteIdx));
          setDeleteIdx(null);
        }}
        title="הסרה"
        message="להסיר מהתבנית?"
        confirmText="הסר"
        cancelText="ביטול"
      />
    </>
  );
}
